import type { AlphabetLetter } from '@/content/mobile';
import type { Dialect, Voice } from '@/providers/LearnerPreferences';

export type AlphabetTab = 'consonants' | 'vowels' | 'tones';

export const ALPHABET_TABS: { id: AlphabetTab; label: string }[] = [
  { id: 'consonants', label: 'Consonants' },
  { id: 'vowels', label: 'Vowels' },
  { id: 'tones', label: 'Tones' },
];

/** Same section titles and sort rules as mobile `AlphabetView`. */
const GROUP_META: Record<string, { tab: AlphabetTab; title: string; sort: boolean }> = {
  'CONSONANTS · PLAIN': { tab: 'consonants', title: 'Single Consonants', sort: true },
  'CONSONANTS · PLAIN_DOUBLE': { tab: 'consonants', title: 'Double Consonants', sort: true },
  'CONSONANTS · PLAIN_TRIPLE': { tab: 'consonants', title: 'Triple Consonants', sort: true },
  'CONSONANTS · QUADRUPLE': { tab: 'consonants', title: 'Quadruple Consonants', sort: true },
  'VOWELS · PLAIN': { tab: 'vowels', title: 'Single Vowels', sort: false },
  'VOWELS · PLAIN_DOUBLE': { tab: 'vowels', title: 'Double Vowels', sort: false },
  TONES: { tab: 'tones', title: 'Tones', sort: false },
};

const GROUP_ORDER = Object.keys(GROUP_META);

export function alphabetFlavorCopy(dialect: Dialect) {
  if (dialect === 'moob') {
    return {
      title: 'Hmong Leng Alphabet',
      description:
        'The Hmong Leng language is a tonal language. A tone marker appears at the end of each syllable, creating a distinct sound.',
    };
  }
  return {
    title: 'Hmong Alphabet',
    description:
      'The Hmong language is a tonal language. A tone marker appears at the end of each syllable, creating a distinct sound.',
  };
}

export function letterAudioSource(letter: AlphabetLetter, voice: Voice) {
  return voice === 'adult' ? letter.sound : letter.soundChildren || letter.sound;
}

export function sectionsForTab(
  alphabet: Record<string, AlphabetLetter[]>,
  tab: AlphabetTab,
): { title: string; letters: AlphabetLetter[] }[] {
  return GROUP_ORDER.flatMap((key) => {
    const meta = GROUP_META[key];
    if (meta.tab !== tab) return [];
    const letters = alphabet[key];
    if (!letters?.length) return [];
    const data = meta.sort ? [...letters].sort((a, b) => a.script.localeCompare(b.script)) : letters;
    return [{ title: meta.title, letters: data }];
  });
}
